import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { ArrowDown, ArrowRight, Play } from "lucide-react";
import { preloadHeroScrub } from "@/lib/heroMedia";

/* ---- Easily swappable media ---- */
const OPEN_POSTER = "/brochure-open-frame.jpg";
const CLOSED_POSTER = "/brochure-closed-frame.jpg";
const INNER_WEBM = "/screen-video.webm"; // the video that plays inside the screen
const INNER_MP4 = "/screen-video.mp4";
const INNER_MP4_FALLBACK = "/hero-video.mp4"; // used until the new clip is uploaded
const INNER_POSTER = "/inner-screen-poster.jpg"; // native screen still (seamless cross-fade)
const INNER_PREVIEW = "/video-preview.jpg"; // custom preview image shown behind the play button

/* ─────────────────────────────────────────────────────────────────────────
   ⚙️  ΟΘΟΝΗ ΒΙΝΤΕΟ — εδώ ρυθμίζεις θέση & μέγεθος του placeholder
   • SCREEN_SIZE : μέγεθος (0–1). Μεγαλύτερο νούμερο = μεγαλύτερο βίντεο. Μένει πάντα 16:9.
   • CX / CY     : θέση κέντρου πάνω στη brochure (0–1). CX = αριστερά→δεξιά, CY = πάνω→κάτω.
   ───────────────────────────────────────────────────────────────────────── */
const SCREEN_SIZE = 0.27; // ← άλλαξε ΜΟΝΟ αυτό για πιο μεγάλο/μικρό (π.χ. 0.30 = ακόμα μεγαλύτερο)
const CX = 0.7075; // κέντρο X της οθόνης (fraction of frame)
const CY = 0.4925; // κέντρο Y της οθόνης (fraction of frame)

/* Auto-computed 16:9 rectangle — μην το πειράζεις, υπολογίζεται από τα παραπάνω */
const SCREEN = {
  left: `${(CX - SCREEN_SIZE / 2) * 100}%`,
  top: `${(CY - SCREEN_SIZE / 2) * 100}%`,
  width: `${SCREEN_SIZE * 100}%`,
  height: `${SCREEN_SIZE * 100}%`,
};
const FINAL_SCALE = 3.5; // how close the screen flies toward the viewport
/* Σε κάθετες οθόνες (κινητά) το 16:9 καρέ μεγαλώνει λίγο πέρα από το πλάτος
   της οθόνης, ώστε το brochure να μη φαίνεται μικροσκοπικό. */
const PORTRAIT_OVERSCAN = 1.2;

/* Edge fade so the video frame blends into the black hero background */
const EDGE_FADE =
  "linear-gradient(to right, black 0%, transparent 10%, transparent 90%, #010101 100%), linear-gradient(to bottom, black 0%, transparent 10%, transparent 90%, black 100%)";

const InnerScreen = ({ visible, framed }) => {
  const [playing, setPlaying] = useState(false);
  const [previewOk, setPreviewOk] = useState(true);
  return (
    <div className="absolute" style={SCREEN} data-testid="hero-inner-screen">
      <div
        className={`relative h-full w-full overflow-hidden transition-opacity duration-500 ${
          visible ? "opacity-100" : "pointer-events-none opacity-0"
        } ${framed ? "rounded-[3px] shadow-2xl ring-1 ring-white/25" : ""}`}
      >
        {playing ? (
          <video
            data-testid="hero-inner-video"
            className="h-full w-full bg-black object-cover"
            poster={previewOk ? INNER_PREVIEW : INNER_POSTER}
            controls
            autoPlay
            playsInline
          >
            <source src={INNER_WEBM} type="video/webm" />
            <source src={INNER_MP4} type="video/mp4" />
            <source src={INNER_MP4_FALLBACK} type="video/mp4" />
          </video>
        ) : (
          <button
            type="button"
            data-testid="hero-inner-play"
            onClick={() => setPlaying(true)}
            aria-label="Αναπαραγωγή βίντεο"
            className="group relative block h-full w-full bg-gradient-to-br from-[#0b0b0c] via-[#120406] to-[#1d0509]"
          >
            {/* Custom preview image filling the brochure screen rectangle */}
            {previewOk && (
              <img
                src={INNER_PREVIEW}
                alt="Προεπισκόπηση βίντεο"
                className="absolute inset-0 h-full w-full object-cover"
                draggable={false}
                onError={() => setPreviewOk(false)}
              />
            )}
            <span
              className="pointer-events-none absolute inset-0 opacity-70"
              style={{
                background:
                  "radial-gradient(75% 75% at 50% 45%, rgba(234,34,48,0.20), transparent 70%)",
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span
                className="absolute animate-ping rounded-full bg-brand-500/40"
                style={{ width: "clamp(15px, 13%, 75px)", aspectRatio: "1" }}
              />
              <span
                className="relative flex items-center justify-center rounded-full bg-brand-500 text-white shadow-lg ring-2 ring-white/70 transition-transform group-hover:scale-110"
                style={{ width: "clamp(15px, 7.5%, 58px)", aspectRatio: "1" }}
              >
                <Play style={{ width: "42%", height: "42%" }} fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

/* Fallback: reduced motion / mobile — open brochure + a clean fullscreen player */
const FallbackHero = () => {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="hero"
      data-testid="hero-static"
      className="relative h-[100svh] w-full overflow-hidden bg-ink text-white"
    >
      <img
        src={OPEN_POSTER}
        alt="VideoActive video brochure"
        className="absolute inset-0 h-full w-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/30 to-ink/90" />
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-5 sm:px-8">
        <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-brand-400/40 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-brand-300">
          VideoActive
        </span>
        <h1 className="max-w-2xl font-display text-4xl font-bold leading-[1.04] sm:text-5xl">
          VIDEO ΣΕ… <span className="text-brand-500">«ΠΑΚΕΤΟ»</span>
        </h1>
        <p className="mt-4 max-w-md text-base text-white/75">
          Άνοιξε το brochure και δες το βίντεο να παίζει στην οθόνη του.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="hero-inner-play"
            onClick={() => setOpen(true)}
            className="group inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-brand-600"
          >
            <Play className="h-4 w-4" fill="currentColor" />
            Δες το βίντεο
          </button>
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            Ζητήστε Προσφορά
          </a>
        </div>
      </div>
      {open && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setOpen(false)}
        >
          <video
            data-testid="hero-inner-video"
            className="max-h-[82vh] w-full max-w-3xl rounded-lg"
            poster={INNER_PREVIEW}
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
          >
            <source src={INNER_WEBM} type="video/webm" />
            <source src={INNER_MP4} type="video/mp4" />
            <source src={INNER_MP4_FALLBACK} type="video/mp4" />
          </video>
        </div>
      )}
    </section>
  );
};

export const HeroScrub = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const targetTime = useRef(0);
  const rafId = useRef(null);
  const dx = useRef(0);
  const dy = useRef(0);
  const maxScale = useRef(FINAL_SCALE);
  const [interactive, setInteractive] = useState(true);
  const [playable, setPlayable] = useState(false);
  const [scrubSrc, setScrubSrc] = useState(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // The scrub plays everywhere (desktop, tablet, mobile); only users who ask
  // for reduced motion get the static hero.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setInteractive(!mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Fully preloaded blob URL → instant, reliable seeking on first visit.
  useEffect(() => {
    if (!interactive) return undefined;
    let alive = true;
    preloadHeroScrub().then((src) => {
      if (alive) setScrubSrc(src);
    });
    return () => {
      alive = false;
    };
  }, [interactive]);

  // Size the 16:9 stage and measure the translate needed to bring the
  // screen-rect center to the viewport center.
  useEffect(() => {
    const measure = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const portrait = vh > vw;
      const W = Math.min(portrait ? vw * PORTRAIT_OVERSCAN : vw, (vh * 16) / 9);
      const H = (W * 9) / 16;
      dx.current = (0.5 - CX) * W;
      dy.current = (0.5 - CY) * H;
      // Zoom in as far as FINAL_SCALE allows, but never let the flying screen
      // grow past ~95% of the viewport width (matters on portrait phones).
      maxScale.current = Math.min(FINAL_SCALE, (0.95 * vw) / (SCREEN_SIZE * W));
      setStage({ w: W, h: H });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    setPlayable(p >= 0.68);
    const v = videoRef.current;
    const d = v?.duration;
    if (!d || Number.isNaN(d)) return;
    const op = Math.min(1, p / 0.4); // brochure opens across first 40%
    targetTime.current = Math.min(d - 0.04, op * d);
  });

  // rAF lerp for buttery continuous scrubbing
  useEffect(() => {
    if (!interactive || !scrubSrc) return undefined;
    const v = videoRef.current;
    if (v) {
      v.pause();
      try {
        v.currentTime = 0;
      } catch (e) {
        /* noop */
      }
    }
    const tick = () => {
      const vid = videoRef.current;
      if (vid && vid.duration && !Number.isNaN(vid.duration)) {
        const cur = vid.currentTime;
        const diff = targetTime.current - cur;
        if (Math.abs(diff) > 0.004 && !vid.seeking) {
          try {
            vid.currentTime = cur + diff * 0.16;
          } catch (e) {
            /* noop */
          }
        }
      }
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
  }, [interactive, scrubSrc]);

  // iOS/Safari paint the first frame only after a play() — prime it silently.
  const primeFirstFrame = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = 0.001;
    } catch (e) {
      /* noop */
    }
    const p = v.play();
    if (p && p.then) {
      p.then(() => v.pause()).catch(() => {});
    }
  };

  // --- choreography ---
  const zoom = useTransform(scrollYProgress, [0.54, 0.95], [0, 1]);
  const scale = useTransform(zoom, (v) => 1 + v * (maxScale.current - 1));
  const tx = useTransform(zoom, (v) => v * dx.current);
  const ty = useTransform(zoom, (v) => v * dy.current);
  const brochureOpacity = useTransform(scrollYProgress, [0.6, 0.86], [1, 0.06]);
  const innerOpacity = useTransform(scrollYProgress, [0.58, 0.76], [0, 1]);

  const introOpacity = useTransform(scrollYProgress, [0, 0.1, 0.2], [1, 1, 0]);
  const introY = useTransform(scrollYProgress, [0, 0.2], [0, -40]);
  // Disable the intro CTA buttons once the intro starts fading, so the
  // invisible (opacity:0) buttons don't capture taps over later phases.
  const introInteractive = useTransform(scrollYProgress, (p) =>
    p < 0.16 ? "auto" : "none"
  );
  const titleOpacity = useTransform(
    scrollYProgress,
    [0.44, 0.5, 0.58, 0.64],
    [0, 1, 1, 0]
  );
  const openTextY = useTransform(scrollYProgress, [0.44, 0.64], [26, -26]);
  const introScrim = useTransform(scrollYProgress, [0.42, 0.52], [1, 0]);
  const cue = useTransform(scrollYProgress, [0, 0.05, 0.12], [1, 1, 0]);

  if (!interactive) return <FallbackHero />;

  return (
    <section
      id="hero"
      ref={ref}
      className="relative"
      style={{ height: "560vh" }}
      data-testid="hero-scrub"
    >
      <div className="sticky top-0 h-[100svh] w-full overflow-hidden bg-ink">
        {/* Zoomable brochure stage */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative"
            style={{
              width: stage.w ? `${stage.w}px` : "min(100vw, calc(100svh * 16 / 9))",
              height: stage.h ? `${stage.h}px` : undefined,
              aspectRatio: "16 / 9",
              scale,
              x: tx,
              y: ty,
              transformOrigin: `${CX * 100}% ${CY * 100}%`,
            }}
          >
            <motion.video
              ref={videoRef}
              src={scrubSrc || undefined}
              style={{ opacity: brochureOpacity }}
              className="absolute inset-0 h-full w-full object-cover"
              poster={CLOSED_POSTER}
              muted
              playsInline
              preload="auto"
              onLoadedMetadata={primeFirstFrame}
            />
            {/* Feather the frame edges into the black background */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute"
              style={{
                opacity: brochureOpacity,
                inset: "-5px",
                background: EDGE_FADE,
              }}
            />
            <motion.div style={{ opacity: innerOpacity }} className="absolute inset-0">
              <InnerScreen visible={playable} framed />
            </motion.div>
          </motion.div>
        </div>

        {/* left scrim for intro legibility */}
        <motion.div
          style={{
            opacity: introScrim,
            background:
              "linear-gradient(100deg, rgba(1,1,1,0.9) 0%, rgba(1,1,1,0.5) 36%, rgba(1,1,1,0) 64%)",
          }}
          className="pointer-events-none absolute inset-0"
        />

        {/* Phase 0 — intro text (closed brochure) */}
        <motion.div
          style={{ opacity: introOpacity, y: introY }}
          className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-7xl flex-col justify-center px-5 sm:px-8"
        >
          <span className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-brand-400/40 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-brand-300">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
            VideoActive
          </span>
          <h1 className="max-w-2xl font-display text-[2.5rem] font-bold leading-[1.02] text-white sm:text-6xl">
            VIDEO ΣΕ…
            <br />
            <span className="text-brand-500">«ΠΑΚΕΤΟ»</span>
          </h1>
          <p className="mt-6 max-w-md text-base text-white/75 sm:text-lg">
            Το απόλυτο εργαλείο πωλήσεων, τώρα στην Ελλάδα από την VideoActive.
          </p>
          <motion.div
            style={{ pointerEvents: introInteractive }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-brand-600"
            >
              Ζητήστε Προσφορά
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#products"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Δες τα προϊόντα
            </a>
          </motion.div>
        </motion.div>

        {/* Phase 1 — title-card dim + open-brochure text (fades as the screen flies in) */}
        <motion.div
          style={{ opacity: titleOpacity }}
          className="pointer-events-none absolute inset-0 z-[9] bg-black/80"
        />
        <motion.div
          style={{ opacity: titleOpacity, y: openTextY }}
          className="pointer-events-none absolute inset-0 z-10 mx-auto flex max-w-7xl flex-col justify-center px-5 sm:px-8"
        >
          <span className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-brand-300">
            Η εμπειρία VideoActive
          </span>
          <h2 className="max-w-xl font-display text-4xl font-bold leading-[1.04] text-white sm:text-6xl">
            Η οθόνη
            <br />
            <span className="text-brand-500">ζωντανεύει.</span>
          </h2>
          <p className="mt-6 max-w-sm text-base text-white/80 sm:text-lg">
            Για λίγα λεπτά, έχεις κερδίσει την απόλυτη προσοχή του παραλήπτη
          </p>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          style={{ opacity: cue }}
          className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
        >
          <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-white/70">
            Scroll για να ανοίξει
          </span>
          <ArrowDown className="h-5 w-5 animate-cue-bounce text-brand-400" />
        </motion.div>
      </div>
    </section>
  );
};
